import { type ReactNode, createContext, forwardRef, useContext } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/cn";

type TabsVariant = "underline" | "pill";

const TabsVariantContext = createContext<TabsVariant>("underline");

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  /** Visual style — underline (default) or pill. */
  variant?: TabsVariant;
}

/**
 * Tabs — controlled tab group (DESIGN-SYSTEM §5). Built on @radix-ui/react-tabs.
 * URL-syncing is intentionally NOT done here — keep it controlled via
 * `value`/`onValueChange` at the call site.
 */
export const Tabs = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  TabsProps
>(({ variant = "underline", className, children, ...props }, ref) => (
  <TabsVariantContext.Provider value={variant}>
    <TabsPrimitive.Root ref={ref} className={cn("w-full", className)} {...props}>
      {children}
    </TabsPrimitive.Root>
  </TabsVariantContext.Provider>
));
Tabs.displayName = "Tabs";

export const TabsList = forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const variant = useContext(TabsVariantContext);
  if (variant !== "underline") {
    return (
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          // Wraps rather than overflows when the pills don't fit one line.
          "inline-flex max-w-full flex-wrap items-center gap-1 rounded-lg bg-bg-secondary p-1",
          className,
        )}
        {...props}
      />
    );
  }
  // More tabs than fit (a phone, a narrow drawer) scroll sideways inside the
  // strip instead of stretching the page. The rule sits on the outer box so
  // the scroller doesn't clip the active tab's underline, which overlaps it.
  return (
    <div className="w-full border-b border-border">
      <TabsPrimitive.List
        ref={ref}
        className={cn(
          "-mb-px flex items-center gap-4 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          className,
        )}
        {...props}
      />
    </div>
  );
});
TabsList.displayName = "TabsList";

export const TabsTrigger = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        "disabled:pointer-events-none disabled:opacity-50",
        variant === "underline"
          ? "shrink-0 border-b-2 border-transparent px-1 pb-2.5 pt-1 text-fg-muted hover:text-fg-primary focus-visible:ring-inset data-[state=active]:border-accent data-[state=active]:text-fg-primary"
          : "rounded-md px-3 py-1.5 text-fg-muted hover:text-fg-primary data-[state=active]:bg-bg-elevated data-[state=active]:text-fg-primary data-[state=active]:shadow-sm",
        className,
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = "TabsTrigger";

export const TabsContent = forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      className,
    )}
    {...props}
  />
));
TabsContent.displayName = "TabsContent";

/** Convenience type for declarative tab arrays. */
export interface TabItem {
  value: string;
  label: ReactNode;
  content?: ReactNode;
}
